import {
  Add,
  Chevron,
  FormCard,
  FormItem,
  Select,
  TextArea,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import {
  type AdjustmentCategoryCode,
  AdjustmentCategoryDescriptionEnum,
  type ImpairmentCode,
  ImpairmentDescriptionEnum
} from '@lmc/validations'
import { Form as AntdForm, Form, type FormInstance } from 'antd'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DeleteButtonText } from '../../../styledComponents'

export const ReasonableAdjustmentsFormCard: FC<{ form: FormInstance<any> }> = ({
  form
}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const reasonableAdjustments = Form.useWatch('reasonableAdjustments', form)

  const handleAddAdjustment = () => {
    const currentAdjustments = form.getFieldValue('reasonableAdjustments') || []
    form.setFieldsValue({
      reasonableAdjustments: [...currentAdjustments, {}]
    })
  }

  const adjustmentCategoryOptions = useMemo(
    () =>
      Object.entries(AdjustmentCategoryDescriptionEnum).map(
        ([code, label]) => ({
          label: <Text>{label}</Text>,
          value: code as AdjustmentCategoryCode
        })
      ),
    []
  )

  const impairmentOptions = useMemo(
    () =>
      Object.entries(ImpairmentDescriptionEnum).map(([code, label]) => ({
        label: <Text>{label}</Text>,
        value: code as ImpairmentCode
      })),
    []
  )

  return (
    <FormCard
      title={t(supConstants.REASONABLE_ADJUSTMENTS)}
      rightChildren={
        !reasonableAdjustments?.length && (
          <Button variant="text" icon={<Add />} onClick={handleAddAdjustment}>
            {t(supConstants.ADD_REASONABLE_ADJUSTMENT)}
          </Button>
        )
      }
    >
      <AntdForm.List name="reasonableAdjustments">
        {(fields, { remove }) => (
          <>
            {fields.map((field) => (
              <FormCard
                key={field.key}
                level={2}
                title={t(supConstants.REASONABLE_ADJUSTMENT)}
                rightChildren={
                  <Button
                    variant="text"
                    icon={<Trash color={theme.color.neutral.color4} />}
                    onClick={() => remove(field.name)}
                  >
                    <DeleteButtonText weight="medium">
                      {t(supConstants.DELETE_REASONABLE_ADJUSTMENT)}
                    </DeleteButtonText>
                  </Button>
                }
              >
                <FormItem
                  label={
                    <Text>{t(supConstants.REASONABLE_ADJUSTMENT_DETAILS)}</Text>
                  }
                  name={[field.name, 'details']}
                >
                  <TextArea
                    autoSize={{
                      minRows: 3
                    }}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Text>
                      {t(supConstants.REASONABLE_ADJUSTMENT_CATEGORY)}
                    </Text>
                  }
                  name={[field.name, 'adjustmentCategory']}
                >
                  <Select
                    mode="multiple"
                    placeholder={t(supConstants.SELECT_ADJUSTMENT_CATEGORY)}
                    options={adjustmentCategoryOptions}
                    suffixIcon={<Chevron direction="down" />}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Text>
                      {t(supConstants.REASONABLE_ADJUSTMENT_IMPAIRMENT)}
                    </Text>
                  }
                  name={[field.name, 'impairment']}
                >
                  <Select
                    mode="multiple"
                    placeholder={t(supConstants.SELECT_IMPAIRMENT)}
                    options={impairmentOptions}
                    suffixIcon={<Chevron direction="down" />}
                  />
                </FormItem>
              </FormCard>
            ))}
          </>
        )}
      </AntdForm.List>
    </FormCard>
  )
}
